import { type IssueResponseBaseUnion } from '@kcftech/prometheus-client';
import { type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import {
    type Normalized,
    type NormalizedQueryOptions,
    normalizedQueryOptions,
} from 'shared/normalized_cache';
import { queryClient } from 'utils/query';

/** The first element of `useIssue`'s query key, `[ISSUE_QUERY_KEY, id]`. */
export const ISSUE_QUERY_KEY = 'issue';

type IssueQueryOptions = Omit<
    NormalizedQueryOptions<
        IssueResponseBaseUnion,
        Error,
        Normalized<IssueResponseBaseUnion>,
        QueryKey
    >,
    'queryKey' | 'queryFn'
>;

/**
 * Query options for the issue, e.g. to `fetchQuery` it. Its data is
 * normalized, so that each fetch updates the issue everywhere it is held.
 */
export function issueQueryOptions(id: string | undefined, options?: IssueQueryOptions) {
    return normalizedQueryOptions({
        ...options,
        queryKey: [ISSUE_QUERY_KEY, id],
        queryFn: id !== undefined ? () => sdClient.getIssue({ id }) : skipToken,
    });
}

export function useIssue(id: string | undefined, options?: IssueQueryOptions) {
    return useQuery(issueQueryOptions(id, options));
}

export function invalidateIssue(id: string) {
    queryClient.invalidateQueries({
        queryKey: [ISSUE_QUERY_KEY, id],
    });
}
